/**
 * AIAnalysisProgress — full post-payment analysis experience.
 *
 * Replaces the static "Analysing" strip with a real progress narrative so
 * paid users always understand:
 *   1. The system is working
 *   2. Roughly how long remains
 *   3. Which stage it's in
 *   4. What they'll receive
 *   5. What to do if it fails
 *
 * Design directive (May 2 2026):
 *   • Always honest — never show 100% before the report is actually ready.
 *   • Soft language — "about N minutes", "usually less than 5", "taking longer
 *     than usual, but still working".
 *   • Staged fallback — if the backend doesn't expose progress, the component
 *     walks a synthetic 0→95% curve so it never freezes on a fixed number.
 *   • Shows "What your AI analysis will show you" panel below the bar so the
 *     wait has visible value.
 *
 * Works for BOTH analysis surfaces:
 *   • Lesson footage review (`LessonReview` page)
 *   • Passport clip AI review (`passport_ai_reviews`)
 */
import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  FileVideo,
  Sparkles,
  Target,
  ClipboardList,
  ShieldCheck,
  Search,
  Brain,
  BarChart3,
  MessageSquareQuote,
  RefreshCw,
  Mail,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ── canonical stage ladder for any analysis backend ──────────────────
// Every upstream status string maps to one of these five stages.
const STAGE_ORDER = ["paid", "preparing", "analysing", "report", "final"];

const STAGE_DEFS = {
  paid:       { label: "Payment confirmed",        pct: 10, icon: CheckCircle2 },
  preparing:  { label: "Preparing video",           pct: 30, icon: FileVideo    },
  analysing:  { label: "Analysing driving behaviour", pct: 60, icon: Target     },
  report:     { label: "Building feedback report",  pct: 85, icon: Sparkles     },
  final:      { label: "Final checks",              pct: 95, icon: ClipboardList },
};

// Map backend statuses to our 5-step ladder. Any status not listed falls back
// to "preparing" so the user never sees a blank state.
const BACKEND_TO_STAGE = {
  // LessonReview.js pipeline
  awaiting_payment: "paid",
  queued:           "paid",
  extracting_audio: "preparing",
  transcribing:     "preparing",
  analysing:        "analysing",
  // passport_phase3 pipeline (single async call)
  pending_review:   "analysing",
  generating:       "report",
  // terminal states (shown by parent, never by this component)
  complete:         "final",
  failed:           "final",
};

/**
 * Soft-language time estimate based on elapsed time and the stage we're on.
 * Never scary, never specific enough to be wrong.
 */
function estimatedTimeMessage(elapsedSec, stageKey) {
  // Rough bracket based on how long we've been running, biased by stage.
  const stageBoost = { paid: 0, preparing: 5, analysing: 15, report: 30, final: 60 };
  const adjusted = elapsedSec + (stageBoost[stageKey] || 0);

  if (adjusted < 30) return "Usually less than 5 minutes";
  if (adjusted < 90) return "About 2 minutes remaining";
  if (adjusted < 180) return "About 1 minute remaining";
  if (adjusted < 300) return "Wrapping up in a moment";
  return "Taking longer than usual, but still working";
}

/**
 * Pick the active stage. Priority:
 *   1. If the backend status maps to a known stage, use it.
 *   2. Otherwise walk a synthetic curve based on elapsed seconds so the bar
 *      still moves forward honestly. Never exceeds 95% until done.
 */
function activeStageKey(backendStatus, elapsedSec) {
  if (backendStatus && BACKEND_TO_STAGE[backendStatus]) {
    return BACKEND_TO_STAGE[backendStatus];
  }
  // Synthetic fallback — paced conservatively.
  if (elapsedSec < 10) return "paid";
  if (elapsedSec < 45) return "preparing";
  if (elapsedSec < 150) return "analysing";
  if (elapsedSec < 240) return "report";
  return "final";
}


/**
 * @param status   Backend status string (matches BACKEND_TO_STAGE keys) or null.
 * @param startedAt ISO datetime when analysis began (so elapsed is honest
 *                  even across page reloads). Falls back to component mount
 *                  time if not provided.
 * @param done      true ⇒ show success state (100% + ✓).
 * @param failed    true ⇒ show failure panel with retry/support actions.
 * @param errorMessage Optional message to show in the failure panel.
 * @param onRetry   Called when the user taps "Retry analysis".
 * @param onContactSupport Called when the user taps "Contact support".
 */
export default function AIAnalysisProgress({
  status,
  startedAt,
  done = false,
  failed = false,
  errorMessage,
  onRetry,
  onContactSupport,
}) {
  // Tick every second so the ETA and synthetic curve update live.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (done || failed) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [done, failed]);

  const elapsedSec = useMemo(() => {
    const start = startedAt ? new Date(startedAt).getTime() : Date.now();
    return Math.max(0, Math.floor((Date.now() - start) / 1000));
  }, [startedAt]);

  const [whatOpen, setWhatOpen] = useState(false);

  // Compute stage values BEFORE any early returns to ensure hooks are called
  // in the same order on every render (React rules of hooks).
  const stageKey = done ? "final" : activeStageKey(status, elapsedSec);
  const pct = done ? 100 : STAGE_DEFS[stageKey].pct;
  const stage = STAGE_DEFS[stageKey];
  const ActiveIcon = stage.icon;
  const etaMessage = done ? "Report ready" : estimatedTimeMessage(elapsedSec, stageKey);

  // May 14, 2026 (operator audit — stuck-at-95 production report):
  // If the bar has been parked at the `final` stage (95%) for more
  // than 5 minutes AND the row isn't already terminal, surface
  // truthful copy telling the user they can leave the page —
  // backend will keep processing. Never fabricate a completion
  // estimate.
  const stuckAtFinal = (
    !done && stageKey === "final" && elapsedSec >= 300
  );
  // Emit a single diagnostic when the threshold first crosses
  // (debounced by the `tick` upper-bound — fires roughly once per
  // session because elapsedSec increases monotonically).
  useEffect(() => {
    if (!stuckAtFinal) return;
    // eslint-disable-next-line no-console
    console.info("[upload-continuity] ai_review_stuck_at_final", {
      elapsed_sec: elapsedSec,
      status,
    });
  }, [stuckAtFinal, elapsedSec, status]);

  if (failed) {
    return (
      <div
        data-testid="ai-analysis-failed"
        className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5 space-y-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center flex-none">
            <AlertCircle className="w-5 h-5 text-rose-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-white">Analysis failed</h3>
            <p className="text-[12.5px] text-zinc-300 mt-1">
              {errorMessage ||
                "We couldn't finish the analysis. Your payment is safe — retry now, or contact support and we'll sort it out."}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {onRetry && (
                <button
                  type="button"
                  data-testid="ai-analysis-retry"
                  onClick={onRetry}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-[12.5px] font-bold ring-1 ring-white/15"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry analysis
                </button>
              )}
              {onContactSupport && (
                <button
                  type="button"
                  data-testid="ai-analysis-support"
                  onClick={onContactSupport}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-transparent border border-white/15 text-zinc-300 text-[12.5px] font-semibold hover:bg-white/5"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Contact support
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="ai-analysis-progress"
      className="space-y-5"
    >
      {/* ── Heading + subtext ─────────────────────── */}
      <div className="space-y-1">
        <h3 className="text-[16px] font-extrabold text-white tracking-tight">
          {done ? "AI analysis ready" : "AI analysis in progress"}
        </h3>
        <p className="text-[12.5px] text-zinc-400">
          {done
            ? "We've finished reviewing your lesson file — your feedback report is below."
            : "We're reviewing your lesson file and building your feedback report."}
        </p>
      </div>

      {/* ── Progress bar + percentage + ETA ───────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center flex-none ${
                done
                  ? "bg-emerald-500/15 border border-emerald-500/30"
                  : "bg-[#D4AF37]/15 border border-[#D4AF37]/30"
              }`}
            >
              <ActiveIcon
                className={`w-4 h-4 ${
                  done ? "text-emerald-400" : "text-[#D4AF37] animate-pulse"
                }`}
              />
            </div>
            <span
              data-testid="ai-analysis-stage-label"
              className="text-[13px] font-bold text-zinc-200 truncate"
            >
              {stage.label}
            </span>
          </div>
          <span
            data-testid="ai-analysis-percent"
            className="text-[12.5px] font-extrabold text-[#F3E5AB] tabular-nums"
          >
            {pct}% complete
          </span>
        </div>

        <div
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-2 rounded-full bg-white/10 overflow-hidden"
        >
          <div
            data-testid="ai-analysis-bar"
            className={`h-full transition-[width] duration-700 ease-out ${
              done
                ? "bg-gradient-to-r from-emerald-400 to-emerald-500"
                : "bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37]"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <p
          data-testid="ai-analysis-eta"
          className="text-[11.5px] text-zinc-500"
        >
          {done ? "✓ Analysis complete" : `Estimated time remaining: ${etaMessage.toLowerCase()}`}
        </p>

        {/* Operator-locked truthful copy when the final-checks stage
            has parked for > 5 minutes. Tells the user they're free
            to leave the page without fabricating an ETA. */}
        {stuckAtFinal && (
          <p
            data-testid="ai-analysis-stuck-at-final"
            className="text-[11.5px] text-amber-300/80 leading-snug"
          >
            Final checks are taking longer than usual. You can leave
            this page — we'll continue processing in the background.
          </p>
        )}
      </div>

      {/* ── 5-step visible breakdown ───────────────── */}
      <ol
        data-testid="ai-analysis-steps"
        className="space-y-1.5"
      >
        {STAGE_ORDER.map((key, idx) => {
          const currentIdx = STAGE_ORDER.indexOf(stageKey);
          const reached = done || idx <= currentIdx;
          const isCurrent = !done && idx === currentIdx;
          const StepIcon = STAGE_DEFS[key].icon;
          return (
            <li
              key={key}
              data-testid={`ai-step-${key}`}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] transition-colors ${
                reached
                  ? "bg-white/[0.035] text-zinc-200"
                  : "bg-transparent text-zinc-600"
              }`}
            >
              <div
                className={`w-5 h-5 rounded-md flex items-center justify-center flex-none ${
                  done
                    ? "bg-emerald-500/15 text-emerald-400"
                    : isCurrent
                    ? "bg-[#D4AF37]/20 text-[#D4AF37] animate-pulse"
                    : reached
                    ? "bg-[#D4AF37]/10 text-[#D4AF37]/80"
                    : "bg-white/5 text-zinc-600"
                }`}
              >
                {done || (reached && !isCurrent) ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <StepIcon className="w-3.5 h-3.5" />
                )}
              </div>
              <span className={isCurrent ? "font-bold text-white" : ""}>
                {STAGE_DEFS[key].label}
              </span>
            </li>
          );
        })}
      </ol>

      {/* ── "While we analyse…" value panel ─────────── */}
      <div
        data-testid="ai-analysis-info-panel"
        className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden"
      >
        <button
          type="button"
          data-testid="ai-analysis-info-toggle"
          onClick={() => setWhatOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.035] transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-4 h-4 text-[#D4AF37] flex-none" />
            <span className="text-[13px] font-bold text-white truncate">
              What your AI analysis will show you
            </span>
          </div>
          {whatOpen ? (
            <ChevronUp className="w-4 h-4 text-zinc-400 flex-none" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-400 flex-none" />
          )}
        </button>
        {whatOpen && (
          <div className="px-4 pb-4 pt-1 space-y-3.5 text-[12.5px] text-zinc-300 leading-relaxed">
            <InfoRow
              icon={Search}
              title="Problem detection"
              body="We identify exactly where things start to go wrong — not just the mistake, but what caused it."
            />
            <InfoRow
              icon={Brain}
              title="Decision breakdown"
              body="We analyse your speed control, positioning, awareness, and timing."
            />
            <InfoRow
              icon={Target}
              title="Key improvement points"
              body="Clear, simple actions: what to do differently, and how to fix it step by step."
            />
            <InfoRow
              icon={BarChart3}
              title="Progress insight"
              body="See patterns across lessons — what's improving, what needs work."
            />
            <InfoRow
              icon={MessageSquareQuote}
              title="Coaching-style feedback"
              body="Not robotic scoring — real guidance explaining why it matters and how it improves your driving."
            />
            <p className="text-[11px] text-zinc-500 italic pt-1 border-t border-white/5">
              This is guidance, not a pass/fail judgement.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-[#D4AF37] flex-none mt-0.5" />
      <div className="min-w-0">
        <p className="text-[12.5px] font-bold text-white">{title}</p>
        <p className="text-[12px] text-zinc-400 mt-0.5">{body}</p>
      </div>
    </div>
  );
}
